import { Middleware } from 'redux';

import { AllActions, clearStore } from '@shared/redux/actions';
import {
  AUTH_DATA_CHANGED,
  authDataChanged,
  loggedOutDueToPasswordChange,
  LOGIN_SUCCESS,
  LOGOUT_FAILURE,
  LOGOUT_SUCCESS,
  logOutByAutotests,
  logOutByTokenRefreshFailed,
  logOutOnIdle,
  logOutWithReasonUnknown,
  PASSWORD_CHANGED,
  SECOND_FACTOR_SUCCESS,
  updateUserData,
} from '@shared/redux/actions/auth-actions';
import { CHRONOMETER_TICK } from '@shared/redux/actions/chronometer-actions';
import { UserDataPayload } from '@shared/redux/actions/payloadTypes/authActionPayloads';
import { getName, subscribeToLogout } from '@shared/util/localStorageAccessor';
import { reportError } from '@shared/util/sentry';
import {
  dropUser,
  getParsedUserClaims,
  initialize,
  isAuthenticated,
  isTokenExpired,
  isUserIdle,
  refreshIfNeeded,
  setUser,
} from '@shared/util/user';

const authMiddleware: Middleware = ({ dispatch }) => {
  initialize(() => dispatch(authDataChanged()));

  // Enable quick logout in UI tests.
  window._logout = () => dispatch(logOutByAutotests());

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', () => {
      // User can go into sleep mode and then focus on web app.
      // Check if token already expired and logout user if it is so.
      if (isTokenExpired()) {
        dispatch(logOutOnIdle());
      }
    });

    subscribeToLogout(() => {
      // User logged out from another tab/window, sync the state in this tab
      const authData = getAuthData();
      dispatch(updateUserData(authData));
    });
  }

  return (next) => (action) => {
    // acton has unknown type, so we need to cast it to AllActions to be able to check its type.
    const appAction = action as AllActions;

    // Authentication storage has to be updated before the action reaches reducers and sagas,
    // because they rely on the stored user data.
    if (appAction.type === LOGIN_SUCCESS || appAction.type === SECOND_FACTOR_SUCCESS) {
      setUser(appAction.payload.userName, appAction.payload.userClaims);
    }

    // No matter if logout succeeded or failed,
    // we need to log user out of webapp.
    if (appAction.type === LOGOUT_SUCCESS || appAction.type === LOGOUT_FAILURE) {
      dropUser();
    }

    next(action);

    // Follow-up actions are dispatched only after the current action has been reduced (call next(action) first).
    // Dispatching them before next(action) starts a nested dispatch that notifies subscribers
    // while the outer action is still on its way to the reducers, so a re-render scheduled by
    // that notification can read the store while the root reducer is executing (Redux error #3).
    if (
      appAction.type === AUTH_DATA_CHANGED ||
      appAction.type === LOGIN_SUCCESS ||
      appAction.type === SECOND_FACTOR_SUCCESS
    ) {
      dispatch(updateUserData(getAuthData()));
    }

    if (appAction.type === LOGOUT_SUCCESS || appAction.type === LOGOUT_FAILURE) {
      dispatch(clearStore());
      dispatch(authDataChanged());
    }

    if (appAction.type === PASSWORD_CHANGED) {
      dispatch(loggedOutDueToPasswordChange());
    }

    if (appAction.type === CHRONOMETER_TICK) {
      refreshIfNeeded()
        .then((result) => {
          if (result === 'logout required') {
            dispatch(logOutByTokenRefreshFailed());
          }
        })
        .catch((error) => {
          reportError(error);
          dispatch(logOutWithReasonUnknown());
        });

      if (isUserIdle() && isAuthenticated()) {
        dispatch(logOutOnIdle());
      }
    }
  };
};

function getAuthData() {
  const userClaims = getParsedUserClaims();
  if (userClaims) {
    const {
      userId,
      user_self_permissions,
      user_other_permissions,
      user_other_orgmanager_permissions,
      org_settings_permissions,
      med_settings_permissions,
      patient_permissions,
      organisation_permissions,
      organisation_other_permissions,
      sd_card_upload_permissions,
      device_settings,
      download_firmware_permissions,
      pic_permissions,
      preregister_device,
    } = userClaims;

    return {
      isAuthenticated: true,
      userName: getName(),
      userId,
      user_self_permissions,
      user_other_permissions,
      user_other_orgmanager_permissions,
      org_settings_permissions,
      organisation_permissions,
      organisation_other_permissions,
      med_settings_permissions,
      sd_card_upload_permissions,
      patient_permissions,
      device_settings,
      download_firmware_permissions,
      pic_permissions,
      preregister_device,
    } as UserDataPayload;
  } else {
    return {
      isAuthenticated: false,
      errorMessage: '',
    } as UserDataPayload;
  }
}

export default authMiddleware;
